/**
 * Configuration utility to get API URL
 * Works for both local development and cloud deployment
 */

// Extend window interface to include our config
declare global {
  interface Window {
    __APP_CONFIG__?: {
      API_URL?: string;
    };
  }
}

export const getApiUrl = (): string => {
  // Priority order:
  // 1. Runtime config from window (set by server/container)
  // 2. Build-time environment variables (for local development)
  // 3. Fallback to localhost
  
  const runtimeApiUrl = window.__APP_CONFIG__?.API_URL;
  const buildTimeApiUrl = import.meta.env.VITE_API_URL || import.meta.env.REACT_APP_API_URL;
  const fallbackUrl = 'http://localhost:8000';
  
  let apiUrl = runtimeApiUrl || buildTimeApiUrl || fallbackUrl;
  
  // Fix mixed content issues: if page is HTTPS but API URL is HTTP, upgrade to HTTPS
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    if (apiUrl.startsWith('http:')) {
      apiUrl = apiUrl.replace('http:', 'https:');
      console.log('🔒 Upgraded API URL from HTTP to HTTPS:', apiUrl);
    }
    // Also handle cases where the URL might not have a protocol
    if (!apiUrl.startsWith('http')) {
      apiUrl = `https://${apiUrl}`;
      console.log('🔒 Added HTTPS protocol to API URL:', apiUrl);
    }
  }
  
  return apiUrl;
};

export const config = {
  get apiUrl() {
    return getApiUrl();
  },
  environment: import.meta.env.VITE_NODE_ENV || import.meta.env.NODE_ENV || 'development',
  isDevelopment: import.meta.env.DEV,
  isProduction: import.meta.env.PROD,
};

// Make config available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).debugConfig = () => {
    console.log('=== Debug Configuration ===');
    console.log('Current API URL:', getApiUrl());
    console.log('Runtime config:', window.__APP_CONFIG__);
    console.log('Build-time env vars:', {
      VITE_API_URL: import.meta.env.VITE_API_URL,
      REACT_APP_API_URL: import.meta.env.REACT_APP_API_URL
    });
    console.log('=== End Debug Configuration ===');
  };
}